import * as React from "react";
import { tx, txr } from "../lib/tx";

/*
 * Flat inline-SVG illustrations for the start-screen reader.
 * Style: mock UI card / labelled diagram / icon chips. No gradients, glows,
 * filters or mascots. Every colour is a flat hex so the file is self-contained.
 * Each art scales with its container (viewBox + width 100%).
 */

export const C = {
  panel: "#141e29",
  panel2: "#19242f",
  code: "#0d141c",
  line: "#26323f",
  text: "#e6edf3",
  soft: "#c9d4de",
  muted: "#93a4b5",
  mint: "#36d1b4",
  amber: "#f2b84b",
  red: "#e5707a",
};

export const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
export const SANS = "inherit";

interface FrameProps {
  label: string;
  aria: string;
  height: number;
  children: React.ReactNode;
}

/** Shared frame: small muted label above a rounded panel, like the mockups. */
export function Frame({ label, aria, height, children }: FrameProps): JSX.Element {
  return (
    <svg
      className="sr-art-svg"
      viewBox={`0 0 360 ${height}`}
      width="100%"
      role="img"
      aria-label={aria}
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block", fontFamily: SANS }}
    >
      <text x="1" y="12" fontSize="11" fill={C.muted}>
        {label}
      </text>
      <rect x="0.5" y="22.5" width="359" height={height - 23} rx="12" fill={C.panel} stroke={C.line} />
      {children}
    </svg>
  );
}

/** Small rounded icon chip with four squares (same glyph as the mockup app tile). */
export function AppChip({ x, y }: { x: number; y: number }): JSX.Element {
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect width="24" height="24" rx="6" fill={C.mint} fillOpacity="0.14" />
      <rect x="6" y="6" width="5" height="5" rx="1.2" fill={C.mint} />
      <rect x="13" y="6" width="5" height="5" rx="1.2" fill={C.mint} />
      <rect x="6" y="13" width="5" height="5" rx="1.2" fill={C.mint} />
      <rect x="13" y="13" width="5" height="5" rx="1.2" fill={C.mint} />
    </g>
  );
}

/* ------------------------------------------------------------------ */
/* 1. assistant: chat line + code snippet + "needs your review" row    */
/* ------------------------------------------------------------------ */
export function AssistantArt(): JSX.Element {
  const H = 296;
  const nb = "\u00a0";
  return (
    <Frame label={tx("Example: AI coding assistant")} aria={tx("A chat request to an AI assistant, the code it wrote, and a note that it needs your review")} height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        {tx("Assistant")}
      </text>
      <rect x="310" y="38" width="34" height="20" rx="10" fill={C.panel2} stroke={C.line} />
      <text x="327" y="52" fontSize="10.5" fontWeight="700" fill={C.mint} textAnchor="middle">
        {tx("AI")}
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      <text x="16" y="87" fontSize="10.5" fill={C.muted}>
        {tx("You")}
      </text>
      <rect x="16" y="93" width="214" height="32" rx="9" fill={C.panel2} stroke={C.line} />
      <text x="29" y="113" fontSize="12.5" fill={C.text}>
        {tx("Build the export feature")}
      </text>
      <rect className="sr-anim-blink" x="196" y="104" width="1.5" height="15" rx="0.75" fill={C.mint} />

      <rect x="16.5" y="138.5" width="327" height="98" rx="8" fill={C.code} stroke={C.line} />
      <text x="29" y="155" fontSize="10.5" fontFamily={MONO} fill={C.muted}>
        {tx("export.ts")}
      </text>
      <text x="330" y="155" fontSize="10.5" fill={C.muted} textAnchor="end">
        {tx("written by the assistant")}
      </text>
      <line x1="17" y1="163.5" x2="343" y2="163.5" stroke={C.line} />
      <text x="29" y="182" fontSize="11.5" fontFamily={MONO} fill={C.soft}>
        {txr("<0>export async function</0> exportOrders(req)", {}, [<tspan fill={C.mint} />])} {"{"}
      </text>
      <text x="29" y="198" fontSize="11.5" fontFamily={MONO} fill={C.soft}>
        {txr("{nb}{nb2}<0>const</0> rows = <1>await</1> db.find(req.user.id);", { nb, nb2: nb }, [<tspan fill={C.mint} />, <tspan fill={C.mint} />])}
      </text>
      <text x="29" y="214" fontSize="11.5" fontFamily={MONO} fill={C.soft}>
        {txr("{nb}{nb2}<0>return</0> toCsv(rows);", { nb, nb2: nb }, [<tspan fill={C.mint} />])}
      </text>
      <text x="29" y="230" fontSize="11.5" fontFamily={MONO} fill={C.soft}>
        {"}"}
      </text>

      <rect x="16.5" y="249.5" width="327" height="30" rx="8" fill={C.amber} fillOpacity="0.1" stroke={C.amber} strokeOpacity="0.4" />
      <circle className="sr-anim-ring" cx="33" cy="264.5" r="5.5" fill="none" stroke={C.amber} strokeWidth="1.5" />
      <circle cx="33" cy="264.5" r="1.8" fill={C.amber} />
      <text x="46" y="268.5" fontSize="12" fontWeight="600" fill={C.amber}>
        {tx("Needs your review before it ships")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. evidence: green PASSED build with a weakened security assertion  */
/* ------------------------------------------------------------------ */
function CheckRow({ y, label, value }: { y: number; label: string; value: string }): JSX.Element {
  return (
    <g>
      <circle cx="26" cy={y} r="7" fill={C.mint} fillOpacity="0.16" />
      <polyline points={`22.5,${y} 25,${y + 2.6} 29.5,${y - 2.8}`} fill="none" stroke={C.mint} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <text x="42" y={y + 4.2} fontSize="12.5" fill={C.text}>
        {label}
      </text>
      <text x="334" y={y + 4.2} fontSize="12" fill={C.muted} textAnchor="end">
        {value}
      </text>
    </g>
  );
}

export function EvidenceArt(): JSX.Element {
  const H = 300;
  return (
    <Frame label={tx("Example: build result")} aria={tx("A build that passed all checks, with a note that a security assertion was weakened")} height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        {tx("Build #482")}
      </text>
      <text x="122" y="53" fontSize="11" fontFamily={MONO} fill={C.muted}>
        {tx("main")}
      </text>
      <rect className="sr-anim-pulse" x="262" y="37" width="82" height="24" rx="12" fill={C.mint} fillOpacity="0.14" stroke={C.mint} strokeOpacity="0.45" style={{ transformOrigin: "303px 49px" }} />
      <polyline points="274,49 277,52 282,46" fill="none" stroke={C.mint} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <text x="290" y="53.5" fontSize="11" fontWeight="700" fill={C.mint} letterSpacing="0.6">
        {tx("PASSED")}
      </text>
      <line x1="16" y1="74.5" x2="344" y2="74.5" stroke={C.line} />

      <CheckRow y={92} label={tx("Unit tests")} value="128 / 128" />
      <line x1="16" y1="106.5" x2="344" y2="106.5" stroke={C.line} strokeOpacity="0.6" />
      <CheckRow y={122} label={tx("Lint")} value="0 issues" />
      <line x1="16" y1="136.5" x2="344" y2="136.5" stroke={C.line} strokeOpacity="0.6" />
      <CheckRow y={152} label={tx("Security tests")} value="24 / 24" />

      <rect x="16.5" y="172.5" width="327" height="112" rx="8" fill={C.red} fillOpacity="0.08" stroke={C.red} strokeOpacity="0.4" />
      <rect x="16" y="178" width="3" height="101" rx="1.5" fill={C.red} />
      <path d="M33 192 l6.5 11 h-13 z" fill="none" stroke={C.red} strokeWidth="1.5" strokeLinejoin="round" />
      <line x1="33" y1="195.6" x2="33" y2="199" stroke={C.red} strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="33" cy="201.2" r="0.9" fill={C.red} />
      <text x="48" y="201" fontSize="12.5" fontWeight="700" fill={C.red}>
        {tx("A security check was weakened")}
      </text>
      <text x="32" y="220" fontSize="11" fill={C.muted}>
        {tx("auth.test.ts changed in this commit. It still passes.")}
      </text>
      <rect x="30" y="230" width="299" height="44" rx="6" fill={C.code} />
      <text x="40" y="247" fontSize="11" fontFamily={MONO} fill={C.red}>
        {tx("- expect(res.status).toBe(403);")}
      </text>
      <text x="40" y="265" fontSize="11" fontFamily={MONO} fill={C.soft}>
        {tx("+ expect(res.status).toBeDefined();")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. glossary: grid of icon chips                                     */
/* ------------------------------------------------------------------ */
type Glyph = "agent" | "context" | "prompt" | "access" | "sql" | "supply" | "secret";

function GlyphShape({ g }: { g: Glyph }): JSX.Element {
  const s = { fill: "none", stroke: C.mint, strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" } as const;
  switch (g) {
    case "agent":
      return (
        <g {...s}>
          <polyline points="-4.5,-4.5 -9,0 -4.5,4.5" />
          <polyline points="4.5,-4.5 9,0 4.5,4.5" />
          <line x1="1.6" y1="-6" x2="-1.6" y2="6" />
        </g>
      );
    case "context":
      return (
        <g {...s}>
          <rect x="-6" y="-8" width="12" height="16" rx="2" />
          <line x1="-3" y1="-3" x2="3" y2="-3" />
          <line x1="-3" y1="0.5" x2="3" y2="0.5" />
          <line x1="-3" y1="4" x2="1" y2="4" />
        </g>
      );
    case "prompt":
      return (
        <g {...s}>
          <rect x="-9" y="-7" width="18" height="14" rx="2.5" />
          <polyline points="-5,-2.5 -1.5,0 -5,2.5" />
          <line x1="1" y1="3" x2="5" y2="3" />
        </g>
      );
    case "access":
      return (
        <g {...s}>
          <rect x="-6.5" y="-1.5" width="13" height="9" rx="2" />
          <path d="M-3.5 -1.5 V-4.5 a3.5 3.5 0 0 1 7 0 V-1.5" />
        </g>
      );
    case "sql":
      return (
        <g {...s}>
          <ellipse cx="0" cy="-5" rx="6.5" ry="2.6" />
          <path d="M-6.5 -5 V5 a6.5 2.6 0 0 0 13 0 V-5" />
          <path d="M-6.5 0 a6.5 2.6 0 0 0 13 0" />
        </g>
      );
    case "supply":
      return (
        <g {...s}>
          <rect x="-10.5" y="-2.8" width="5.6" height="5.6" rx="1.2" />
          <rect x="-2.8" y="-2.8" width="5.6" height="5.6" rx="1.2" />
          <rect x="4.9" y="-2.8" width="5.6" height="5.6" rx="1.2" />
        </g>
      );
    case "secret":
      return (
        <g {...s}>
          <circle cx="-4" cy="0" r="3.8" />
          <line x1="-0.2" y1="0" x2="9" y2="0" />
          <line x1="5.6" y1="0" x2="5.6" y2="3.4" />
          <line x1="9" y1="0" x2="9" y2="3.4" />
        </g>
      );
  }
}

interface ChipSpec {
  g: Glyph;
  title: string;
  sub: string;
}

const CHIPS: ChipSpec[] = [
  { g: "agent", title: "AI coding agent", sub: "writes code for you" },
  { g: "context", title: "Context", sub: "what you share" },
  { g: "prompt", title: "Prompt injection", sub: "hidden instructions" },
  { g: "access", title: "Access control", sub: "IDOR" },
  { g: "sql", title: "SQL & XSS", sub: "untrusted input" },
  { g: "supply", title: "Supply chain", sub: "third-party code" },
  { g: "secret", title: "Secret", sub: "keys and tokens" },
];

export function GlossaryArt(): JSX.Element {
  const H = 298;
  return (
    <Frame label={tx("Terms at a glance")} aria={tx("Seven terms shown as labelled icon chips: AI coding agent, context, prompt injection, access control, SQL and XSS, supply chain and secret")} height={H}>
      {CHIPS.map((c, i) => {
        const last = i === CHIPS.length - 1;
        const col = i % 2;
        const row = Math.floor(i / 2);
        const x = last ? 16 : 16 + col * 172;
        const y = 38 + row * 64;
        const w = last ? 328 : 156;
        return (
          <g key={c.g} transform={`translate(${x} ${y})`}>
            <rect x="0.5" y="0.5" width={w - 1} height="51" rx="10" fill={C.panel2} stroke={C.line} />
            <circle cx="26" cy="26" r="16" fill={C.mint} fillOpacity="0.12" />
            <g transform="translate(26 26)">
              <GlyphShape g={c.g} />
            </g>
            <text x="51" y="23" fontSize="12.5" fontWeight="700" fill={C.text}>
              {c.title}
            </text>
            <text x="51" y="39" fontSize="10.5" fill={C.muted}>
              {c.sub}
            </text>
          </g>
        );
      })}
    </Frame>
  );
}

